import {Box, BoxProps, styled} from "@mui/joy";
import {ForwardedRef, ReactNode} from "react";
import {DefaultColorPalette, DefaultPaletteRange} from "@mui/joy/styles/types";

export type VeiledChildProps<T extends BoxProps = BoxProps> = Omit<T, "children"> & {
    ref?: ForwardedRef<HTMLDivElement> | null;
}

export type VeiledProps<T extends BoxProps = BoxProps> = VeiledChildProps<T> & {
    child: (props: VeiledChildProps<T>) => ReactNode;
    variant?: "soft" | "solid";
    baseZIndex?: number;
    blurRadius?: number | string;
    primerBackgroundColor?: string;
    primerOpacity?: number;
    topcoatGradientAngle?: string;
    topcoatGradientColor1?: string;
    topcoatGradientColor2?: string;
    topcoatGradientPalette?: DefaultColorPalette;
    topcoatGradientStripeWidth?: number;
    topcoatOpacity?: number;
    lifted?: boolean;
}

const DEFAULT_BASE_ZINDEX = 0;
const GRID_AREA = "1 / 1 / 2 / 2";
const DEFAULT_BLUR_RADIUS = "0.8px";
const DEFAULT_PRIMER_OPACITY = 0.08;
const DEFAULT_TOPCOAT_OPACITY = 0.2;
const DEFAULT_THIN_STRIPE_PIXELS = 12;
const DEFAULT_MARGIN = "3px";

const VeiledRoot
    = styled(Box, {
    name: "Veiled",
    slot: "root",
})<{ ownerState: VeiledProps }>(({ownerState}) => {
    return {
        // width: "100%",
        // height: "100%",
        display: "grid",
        gridTemplateColumns: "1fr",
        zIndex: ownerState.baseZIndex ?? DEFAULT_BASE_ZINDEX,
    };
});

const VeiledGlass
    = styled("div", {
    name: "Veiled",
    slot: "glass",
})<{ ownerState: VeiledProps }>(({ownerState}) => {
    return {
        width: "100%",
        height: "100%",
        gridArea: GRID_AREA,
        zIndex: (ownerState.baseZIndex ?? DEFAULT_BASE_ZINDEX) + 1,
        ...(!ownerState.lifted
            ? {backdropFilter: `blur(${(ownerState.blurRadius ?? DEFAULT_BLUR_RADIUS).toString()})`}
            : {}),
    };
});

const VeiledPrimer
    = styled("div", {
    name: "Veiled",
    slot: "primer",
})<{ ownerState: VeiledProps }>(({theme, ownerState}) => {
    return {
        width: "100%",
        height: "100%",
        gridArea: GRID_AREA,
        zIndex: (ownerState.baseZIndex ?? DEFAULT_BASE_ZINDEX) + 2,
        backgroundColor: ownerState.primerBackgroundColor ?? theme.palette.neutral[100],
        opacity: ownerState.primerOpacity ?? DEFAULT_PRIMER_OPACITY,
    };
});

const topcoatVariantShades: Record<"soft" | "solid", Record<"light" | "dark", keyof DefaultPaletteRange>> = {
    soft: {light: 50, dark: 100},
    solid: {light: 300, dark: 500},
};

const VeiledTopcoat
    = styled("div", {
    name: "Veiled",
    slot: "topcoat",
})<{ ownerState: VeiledProps }>(({theme, ownerState}) => {
    const rawSingleStripeWidth = ownerState.topcoatGradientStripeWidth ?? DEFAULT_THIN_STRIPE_PIXELS;
    const singleStripeWidth = `${rawSingleStripeWidth.toString()}px`;
    const doubleStripeWidth = `${(rawSingleStripeWidth * 2).toString()}px`;
    const stripePalette = theme.palette[ownerState.topcoatGradientPalette ?? "neutral"];
    const variant = ownerState.variant ?? "soft";
    const stripeLightColor = ownerState.topcoatGradientColor1 ?? stripePalette[topcoatVariantShades[variant].light];
    const stripeDarkColor = ownerState.topcoatGradientColor2 ?? stripePalette[topcoatVariantShades[variant].dark];
    return {
        width: "100%",
        height: "100%",
        gridArea: GRID_AREA,
        zIndex: (ownerState.baseZIndex ?? DEFAULT_BASE_ZINDEX) + 3,
        background: `repeating-linear-gradient(
            ${ownerState.topcoatGradientAngle ?? "135deg"},
            ${stripeLightColor},
            ${stripeLightColor} ${singleStripeWidth},
            ${stripeDarkColor} ${singleStripeWidth},
            ${stripeDarkColor} ${doubleStripeWidth}
            );`,
        opacity: ownerState.topcoatOpacity ?? DEFAULT_TOPCOAT_OPACITY,
    };
});

export function Veiled(inProps: VeiledProps) {
    // const props = useThemeProps({props: inProps, name: "Veil"});
    const props = inProps;
    const live = !!props.lifted;
    let ownerState = props;
    const childProps = {
        ...props,
        ...(!live ? {disabled: true, ref: null} : {}),
    };
    if (props.lifted) {
        ownerState = {...ownerState, primerOpacity: 0, topcoatOpacity: 0};
    }

    return <VeiledRoot ownerState={ownerState} ref={live ? null : props.ref} sx={props.sx}>
        <Box sx={{
            display: "flex", flexDirection: "column",
            alignItems: "stretch", justifyContent: "center",
            gridArea: GRID_AREA, px: DEFAULT_MARGIN, py: DEFAULT_MARGIN,
        }}>
            {props.child(childProps)}
        </Box>
        {!props.lifted
            ? <>
                <VeiledGlass ownerState={ownerState}/>
                <VeiledPrimer ownerState={ownerState}/>
                <VeiledTopcoat ownerState={ownerState}/>
            </>
            : null}
    </VeiledRoot>
}

// export function Veiled<T extends BoxProps>(props: VeiledProps<T>, ref: ForwardedRef<HTMLDivElement>) {
//     return <UVeiled {...props as VeiledProps} ref={ref}/>;
// }
//





